import { Loader } from '@ur-solutions/react-components'
import type { FirmadokIcon} from '@/components_deprecated';
import { SVGIcon } from '@/components_deprecated'
import React, { useCallback } from 'react'
import { NavLink } from 'react-router'
import styled, { keyframes } from 'styled-components'

const growBackground = keyframes`
  from {
    top: 31px;
    left: 31px;
    width: 0%;
    height: 0%;
  }
  to {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`

const Badge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 28px;
  padding: 0 16px;
  min-width: 54px;

  border-radius: 999px;
  font-size: 0.9em;
  color: ${props => props.theme.colors.gray2};
  background-color: ${props => props.theme.colors.tertiary}55;
`

interface NavLinkProps {
  $marginOverride?: string
  $iconBoxWidth?: string
}

const Item = styled(NavLink)<NavLinkProps>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 3.875rem;
  padding: 0 16px 0 0;

  color: ${props => props.theme.colors.gray4};
  border-radius: 16px;

  & > div {
    display: flex;
    align-items: center;
  }

  span {
    margin-left: ${props => props.$marginOverride ?? '1em'};
  }
  /* A fixed slot for icons whose glyph is not the same shape as the rest, so
     they line up with them instead of sitting flush against their own box. */
  & > div > span:first-child {
    ${props => props.$iconBoxWidth && `width: ${props.$iconBoxWidth};`}
  }
  svg,
  span {
    transition: transform 0.2s ease-out;
  }

  &::before {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 0;
    height: 0;

    background-color: ${props => props.theme.colors.primary};
    border-radius: 16px;
  }

  &:hover {
    text-decoration: none;

    span {
      font-weight: 600;
    }
    svg {
      fill: black;
    }
  }

  &.active {
    &::before {
      content: '';
      animation: ${growBackground} 200ms ease-out forwards;
    }

    svg {
      fill: white;
    }
    span {
      color: white;
      font-weight: 600;
    }

    ${Badge} {
      color: white;
      font-weight: 600;
      background-color: ${props => props.theme.colors.tertiary}aa;
    }
  }
`

export interface SidebarNavItemProps {
  className?: string

  to: string
  end?: boolean
  target?: string
  text: string
  badge?: string | number
  badgeLoading?: boolean

  icon: FirmadokIcon
  iconWidth?: string
  /**
   * Width of the slot the icon is centred in, when it differs from the icon's
   * own width. Lets a glyph be drawn smaller without pulling itself and the
   * label out of line with the items around it.
   */
  iconBoxWidth?: string
  marginOverride?: string

  onBeforeNavigate?(): void
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  className,

  to,
  target,
  text,
  badge,
  badgeLoading = false,

  icon,
  iconWidth = '28px',
  iconBoxWidth,
  marginOverride,

  end,

  onBeforeNavigate,
}) => {
  const handleClick = useCallback(
    (evt: React.MouseEvent) => {
      onBeforeNavigate?.()
      if (!to.startsWith('http')) return

      evt.preventDefault()
      if (target !== '_blank') window.location.href = to
      else window.open(to, target)
    },
    [onBeforeNavigate, target, to]
  )

  return (
    <Item
      className={className}
      to={to}
      end={end}
      target={target}
      $marginOverride={marginOverride}
      $iconBoxWidth={iconBoxWidth}
      onClick={handleClick}
    >
      <div>
        <SVGIcon icon={icon} width={iconWidth} fill="gray4" />
        <span>{text}</span>
      </div>

      {typeof badge !== 'undefined' && (
        <Badge>{badgeLoading ? <Loader.Dots size={26} /> : badge}</Badge>
      )}
    </Item>
  )
}
